import { toast } from 'sonner';

interface ProviderConnectionErrorOptions {
  provider: string;
  providerLabel?: string;
  error: string;
  onRetry: () => void;
}

function fallbackProviderLabel(provider: string): string {
  return provider.charAt(0).toUpperCase() + provider.slice(1);
}

/**
 * Keep OAuth connection failures actionable across popup and full-page flows.
 * Retrying starts a fresh, session-bound link attempt; Exit dismisses the
 * toast and leaves the signed-in user on Settings.
 */
export function showProviderConnectionError({
  provider,
  providerLabel = fallbackProviderLabel(provider),
  error,
  onRetry,
}: ProviderConnectionErrorOptions): void {
  const description =
    error === 'identity_already_linked'
      ? `This ${providerLabel} account is already linked to a different Reflex user. Retry with a different ${providerLabel} account, or exit to keep using your current user.`
      : error === 'domain_not_allowed'
        ? `Your ${providerLabel} email is not allowed on this Reflex instance.`
        : error === 'sso_unavailable'
          ? `Your organization doesn't have single sign-on set up. Ask an organization admin to configure it, then retry.`
          : error === 'invalid_state'
            ? `The connection expired before ${providerLabel} finished. Retry to start a new one.`
            : `${providerLabel} returned an error (${error}). Retry the connection, or exit to keep using your current user.`;

  toast.error(`Could not connect ${providerLabel}`, {
    description,
    action: { label: 'Retry', onClick: onRetry },
    cancel: { label: 'Exit', onClick: () => undefined },
    duration: Infinity,
  });
}
